import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Stack,
  Typography
} from '@mui/material';
import moment from 'moment-timezone';
import { useFormikContext } from 'formik';
import { DATE_FORMAT } from 'src/utils/constants';
import { getStoredUserTimezone } from 'src/utils/timezoneUtils';

const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last-7-days', label: 'Last 7 days' },
  { id: 'last-30-days', label: 'Last 30 days' },
];

const getPresetRange = (presetId, timezoneName) => {
  const now = moment().tz(timezoneName).seconds(0).milliseconds(0);
  const today = now.clone().startOf('day');

  if (presetId === 'yesterday') {
    const yesterday = today.clone().subtract(1, 'day');
    return { start: yesterday, end: yesterday.clone().endOf('day') };
  }

  if (presetId === 'last-7-days') {
    return { start: today.clone().subtract(6, 'days'), end: now };
  }

  if (presetId === 'last-30-days') {
    return { start: today.clone().subtract(29, 'days'), end: now };
  }

  return { start: today, end: now };
};

function DateRangeShortcuts({
  startName, endName, onlyDate = false, dateFormat
}) {
  const { setFieldValue } = useFormikContext();
  const format = dateFormat || (onlyDate ? DATE_FORMAT.unixDate : DATE_FORMAT.unixDateTime);

  const applyPreset = useCallback((presetId) => {
    const range = getPresetRange(presetId, getStoredUserTimezone());
    setFieldValue(startName, range.start.format(format), true);
    setFieldValue(endName, range.end.format(format), true);
  }, [endName, format, onlyDate, setFieldValue, startName]);

  return (
    <Stack spacing={0.75} sx={{ width: '100%', minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Quick date range
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        role="group"
        aria-label="Quick date range"
        sx={{
          width: '100%',
          minWidth: 0,
          '& .MuiButton-root': {
            flex: '0 0 auto',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            minHeight: 36
          }
        }}
      >
        {PRESETS.map((preset) => (
          <Button
            key={preset.id}
            type="button"
            variant="outlined"
            size="small"
            onClick={() => applyPreset(preset.id)}
          >
            {preset.label}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
}

DateRangeShortcuts.propTypes = {
  startName: PropTypes.string.isRequired,
  endName: PropTypes.string.isRequired,
  onlyDate: PropTypes.bool,
  dateFormat: PropTypes.string
};

export default DateRangeShortcuts;
